"use client";

import { Upload, AlertTriangle } from "lucide-react";
import React, { useState, useEffect } from "react";
import type { FileWithPath } from "react-dropzone";

import RenderIf from "@/app/_module/components/RenderIf";
import { Button } from "@/app/_module/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import {
  Dialog,
  DialogTrigger,
  ResponsiveDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/app/_module/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/app/_module/components/ui/alert";
import { Input } from "@/app/_module/components/ui/input";

import ImportComplete from "./ImportComplete";

import { MapImport, ProgressSteps, UploadCSV } from ".";
import { useQueryClient } from "@tanstack/react-query";
import { getApiUrl } from "@/app/_module/services/config";
import supabaseAuthService from "@/app/_module/services/supabaseAuthService";
import { usePipelineStages } from "../../hooks/queries";
import { isDemoMode } from "@/app/_module/utils/demoMode";

// Import usage response type
interface ImportUsageData {
  currentUsage: number;
  limit: number; // -1 means unlimited
  remaining: number; // -1 means unlimited
  limitReached: boolean;
  accountType: string;
  isTrial: boolean;
  periodType: 'trial' | 'monthly';
  periodStart: string;
  periodEnd: string;
  resetMessage: string;
}

function getTitleHeaders(step: TCurrentStep): {
  title: string;
  description: string;
} {
  switch (step) {
    case "upload":
      return {
        title: isDemoMode() ? "Import Leads" : "Upload File",
        description: isDemoMode()
          // The sandbox has no file picker, so don't tell the visitor to upload one.
          ? "Import a sample lead list and match its headers, exactly as you would with your own file"
          : "Upload your CSV, XLSX, or XLS file and match headers to import your data",
      };
    case "mapping":
      return {
        title: "Map Columns",
        description: "Review and match your CSV columns to the system fields",
      };
    case "complete":
      return {
        title: "Import Complete",
        description: "Your data has been successfully imported into the system",
      };
  }
}

interface UploadLeadsModalProps {
  externalOpen?: boolean;
  onExternalOpenChange?: (open: boolean) => void;
}

const UploadLeadsModal = function ({ externalOpen, onExternalOpenChange }: UploadLeadsModalProps = {}) {
  const queryClient = useQueryClient();
  const [internalOpen, setInternalOpen] = useState(false);

  // Use external control if provided, otherwise use internal state
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  const setIsOpen = (open: boolean) => {
    if (onExternalOpenChange) {
      onExternalOpenChange(open);
    } else {
      setInternalOpen(open);
    }
  };
  const [currentStep, setCurrentStep] = useState<TCurrentStep>("upload");

  // Import usage state
  const [importUsage, setImportUsage] = useState<ImportUsageData | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  // Fetch import usage when dialog opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchImportUsage = async () => {
      setUsageLoading(true);
      try {
        const session = await supabaseAuthService.getCurrentSession();
        const token = session?.access_token;
        if (!token) {
          console.error('No auth token available');
          return;
        }
        const response = await fetch(getApiUrl('leads/import/usage'), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const result = await response.json();
          setImportUsage(result.data);
        }
      } catch (error) {
        console.error('Failed to fetch import usage:', error);
      } finally {
        setUsageLoading(false);
      }
    };

    fetchImportUsage();
  }, [isOpen]);

  const [csvFile, setCsvFile] = useState<FileWithPath | null>(null);
  const [csvData, setCsvData] = useState<unknown[] | null>(null);
  const [importedRowCount, setImportedRowCount] = useState<number>(0);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  // Track if file has headers and detected column types (for headerless files)
  const [fileHasHeaders, setFileHasHeaders] = useState<boolean>(true);
  const [detectedTypes, setDetectedTypes] = useState<Record<string, string> | null>(null);
  // Pipeline stage for imported leads (empty = use backend default "New lead")
  const [importPipelineStatus, setImportPipelineStatus] = useState<string>("");
  // Lead list name for tagging imported leads (e.g., "Caboom Leads")
  const [importListName, setImportListName] = useState<string>("");
  const { data: pipelineStages } = usePipelineStages();

  const { title, description } = getTitleHeaders(currentStep);

  const cancelImport = () => {
    setCsvData(null);
    setImportedRowCount(0);
    setCsvHeaders([]);
    setMappings({});
    setFileHasHeaders(true);
    setDetectedTypes(null);
    setImportPipelineStatus("");
    setImportListName("");
    setCurrentStep("upload");
    setIsOpen(false);
  };

  const resetImport = () => {
    setCsvData(null);
    setImportedRowCount(0);
    setCsvHeaders([]);
    setMappings({});
    setFileHasHeaders(true);
    setDetectedTypes(null);
    setImportPipelineStatus("");
    setImportListName("");
    setCurrentStep("upload");
  };
  const handleImportComplete = async () => {
    await queryClient.invalidateQueries({ queryKey: ["leads"] });
  };

  return (
    <Dialog onOpenChange={setIsOpen} open={isOpen}>
      <DialogTrigger asChild>
        <Button className="transition-all duration-200 hover:scale-105" size="sm" variant="outline">
          <Upload className="h-4 w-4 mr-2" />
          Upload File
        </Button>
      </DialogTrigger>
      <ResponsiveDialogContent size="3xl" className="m-upload-modal max-h-[90vh] flex flex-col p-0">
        {/* Fixed Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
          <div className="mt-4">
            <ProgressSteps currentStep={currentStep} />
          </div>
        </DialogHeader>

        {/* Scrollable Content Area — flex-auto NOT flex-1: basis-0 children
            collapse to 0px inside the shell's content-sized dialog */}
        <div className="flex-auto min-h-0 overflow-y-auto px-6 py-4">
          {/* Import Usage Warning - Limit Reached */}
          <RenderIf condition={Boolean(!usageLoading && importUsage?.limitReached)}>
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Lead Import Limit Reached</AlertTitle>
              <AlertDescription>
                {importUsage?.isTrial ? (
                  <>
                    You have used all {importUsage?.limit?.toLocaleString()} leads available during your trial period.
                    {importUsage?.resetMessage && ` ${importUsage.resetMessage}`}
                  </>
                ) : (
                  <>
                    You have used all {importUsage?.limit?.toLocaleString()} leads for this {importUsage?.periodType === 'monthly' ? 'billing period' : 'period'}.
                    {importUsage?.resetMessage && ` ${importUsage.resetMessage}`}
                  </>
                )}
              </AlertDescription>
            </Alert>
          </RenderIf>

          {/* Import Usage Info - Not at limit but showing progress */}
          <RenderIf condition={Boolean(!usageLoading && importUsage && !importUsage.limitReached && importUsage.limit !== -1 && currentStep === "upload" && !['full_fledged', 'super_agent', 'enterprise'].includes(importUsage.accountType))}>
            <div className="mb-4 p-3 bg-muted rounded-lg">
              <div className="flex justify-between items-center text-sm mb-2">
                <span className="text-muted-foreground">
                  {importUsage?.isTrial ? 'Trial Import Usage' : 'Monthly Import Usage'}
                </span>
                <span className="font-medium">
                  {importUsage?.currentUsage?.toLocaleString()} / {importUsage?.limit?.toLocaleString()} leads
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    importUsage && importUsage.currentUsage / importUsage.limit > 0.8
                      ? 'bg-orange-500'
                      : 'bg-primary'
                  }`}
                  style={{ width: `${Math.min(100, ((importUsage?.currentUsage || 0) / (importUsage?.limit || 1)) * 100)}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {importUsage?.remaining?.toLocaleString()} leads remaining. {importUsage?.resetMessage}
              </p>
            </div>
          </RenderIf>

          {/* Pipeline + Lead List Name controls — rendered for both upload AND
              mapping steps so the agent can rename the batch / change the
              pipeline stage at any point before clicking Import. Hidden once
              the import has actually completed. */}
          <RenderIf condition={currentStep !== "complete" && !importUsage?.limitReached}>
            {/* Pipeline Stage Selector */}
            {pipelineStages && pipelineStages.length > 0 && (
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Import leads into pipeline
                </label>
                <Select
                  value={importPipelineStatus}
                  onValueChange={setImportPipelineStatus}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Default (Leads / New lead)" />
                  </SelectTrigger>
                  <SelectContent>
                    {pipelineStages.map((stage) => (
                      <SelectItem key={stage.id} value={stage.systemValue || stage.name}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: stage.color }}
                          />
                          {stage.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  All imported leads will be assigned to this pipeline stage.
                </p>
              </div>
            )}
            {/* Lead List Name Input */}
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                Lead List Name
              </label>
              <Input
                value={importListName}
                onChange={(e) => setImportListName(e.target.value)}
                placeholder="Lead Batch"
                className="w-full"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Name this import batch to easily filter and identify these leads later.
              </p>
            </div>
          </RenderIf>

          <RenderIf condition={currentStep === "upload"}>
            {importUsage?.limitReached ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">
                  You cannot upload more leads until your limit resets or you upgrade your plan.
                </p>
                <Button variant="outline" onClick={cancelImport}>
                  Close
                </Button>
              </div>
            ) : (
              <UploadCSV
                cancelImport={cancelImport}
                setCsvData={setCsvData}
                setCsvFile={setCsvFile}
                setCsvHeaders={setCsvHeaders}
                setCurrentStep={setCurrentStep}
                setMappings={setMappings}
                setFileHasHeaders={setFileHasHeaders}
                setDetectedTypes={setDetectedTypes}
                importUsage={importUsage}
              />
            )}
          </RenderIf>

          <RenderIf condition={currentStep === "mapping"}>
            <MapImport
              cancelImport={cancelImport}
              csvData={csvData as Record<string, unknown>[] | null}
              csvFile={csvFile}
              csvHeaders={csvHeaders}
              mappings={mappings}
              setCurrentStep={setCurrentStep}
              setMappings={setMappings}
              onImportComplete={handleImportComplete}
              queryClient={queryClient}
              fileHasHeaders={fileHasHeaders}
              detectedTypes={detectedTypes}
              defaultStatus={importPipelineStatus || undefined}
              setImportedRowCount={setImportedRowCount}
              listName={importListName || undefined}
            />
          </RenderIf>

          <RenderIf condition={currentStep === "complete"}>
            <ImportComplete
              cancelImport={cancelImport}
              csvData={csvData}
              resetImport={resetImport}
              importedRowCount={importedRowCount}
            />
          </RenderIf>
        </div>
      </ResponsiveDialogContent>
    </Dialog>
  );
};

export type TCurrentStep = "upload" | "mapping" | "complete";

export default UploadLeadsModal;